import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { RichTextDisplay } from "@/components/RichTextEditor";
import SmartRichText from "@/components/SmartRichText";
import { MessageSquare, CornerDownRight, Edit3, Trash2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative, fmtAbsRelative } from "@/lib/format";

/**
 * <CommentThread />
 * Reusable threaded discussion component.
 *
 * Two modes:
 *   - Generic via entityType + entityId (uses /threads endpoints — works for
 *     milestone, meeting, raid, task, review_item).
 *   - Custom endpoints (legacy task_comments / raid_comments) by passing
 *     `customEndpoints`.
 */
export default function CommentThread({
  entityType,
  entityId,
  customEndpoints,
  title = "Discussion",
  emptyHint = "Start the conversation.",
  testid = "comment-thread",
  extraToolbar = null,
  // R280.3 · Optional per-comment footer renderer. Receives
  // ({ c, reload }) and returns JSX rendered under the comment body.
  // Used by StudioBooking to show a one-tap "Accept reschedule" button
  // on comments where meta.kind === "reschedule_request".
  renderCommentFooter = null,
}) {
  const { user, activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 253r · Ticket-only external toggle — when true, the comment is
  // emailed to the requester (see routes_threads.py).
  const [isExternal, setIsExternal] = useState(false);

  const endpoints = customEndpoints || {
    list: `/threads/${entityType}/${entityId}/comments`,
    post: `/threads/${entityType}/${entityId}/comments`,
    patch: (cid) => `/thread-comments/${cid}`,
    del: (cid) => `/thread-comments/${cid}`,
  };

  const load = async () => {
    try {
      const { data } = await api.get(endpoints.list);
      setRows(data || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (entityId) load(); }, [entityType, entityId]);

  // Build tree from flat list
  const tree = useMemo(() => buildTree(rows), [rows]);

  const submit = async (e) => {
    e?.preventDefault();
    const text = (draft || "").trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(endpoints.post, {
        body: text, parent_id: replyTo?.id || null,
        is_external: (entityType === "ticket" || entityType === "studio_booking") ? isExternal : false,
      });
      setDraft(""); setReplyTo(null); setIsExternal(false);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't post"));
    } finally { setBusy(false); }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditBody(c.body || ""); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };
  const saveEdit = async () => {
    if (!editBody.trim()) return;
    try {
      await api.patch(endpoints.patch(editingId), { body: editBody });
      cancelEdit();
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Edit failed")); }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(endpoints.del(c.id));
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const renderNode = (node, depth = 0) => {
    const c = node.comment;
    const mine = c.author_id === user?.id;
    const isEditing = editingId === c.id;
    // Iter 253w · System messages (task audit trail entries) render as a
    // dimmer, italicised, non-actionable line so the thread reads like a
    // real audit log: "assignee: Alice → Bob" without avatars or reply/edit.
    const isSystem = c.kind === "system";
    if (isSystem) {
      return (
        <div key={c.id} style={{ marginLeft: depth * 20 }}
             className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground italic bg-muted/30 border-l-2 border-primary/40 px-2 py-1"
             data-testid={`${testid}-system-${c.id}`}>
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest not-italic font-semibold text-primary/70 bg-background border border-primary/20 px-1.5 py-0.5 rounded-none shrink-0">
            system
          </span>
          <span className="font-medium not-italic text-foreground/80 shrink-0">{c.author_name}</span>
          <span className="truncate">{c.body_text || c.body}</span>
          <span className="text-[10px] ml-auto whitespace-nowrap shrink-0" title={c.created_at}>{fmtAbsRelative(c.created_at)}</span>
        </div>
      );
    }
    return (
      <div key={c.id} style={{ marginLeft: depth * 20 }} className="border-l-2 border-border pl-3 mt-2" data-testid={`${testid}-comment-${c.id}`}>
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium">{c.author_name}</span>
          <span className="text-[10px] text-muted-foreground" title={c.created_at}>
            {fmtAbsRelative(c.created_at)}{c.edited_at ? " · edited" : ""}
          </span>
        </div>
        {isEditing ? (
          <div className="mt-1 space-y-2">
            <SmartRichText value={editBody} onChange={setEditBody} minRows={2} maxHeight={280} mediaMode="url-only" workspaceId={activeWorkspaceId} testid={`${testid}-edit-editor-${c.id}`} />
            <div className="flex gap-2">
              <Button size="sm" className="rounded-none h-7 text-xs" onClick={saveEdit} data-testid={`${testid}-edit-save-${c.id}`}>Save</Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={cancelEdit}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <RichTextDisplay html={c.body} className="mt-1" testid={`${testid}-body-${c.id}`} />
            {renderCommentFooter && renderCommentFooter({ c, reload: load })}
            <div className="flex items-center gap-3 mt-1 text-[10px]">
              <button onClick={() => setReplyTo(c)} className="text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`${testid}-reply-${c.id}`}>
                <CornerDownRight className="w-3 h-3" /> Reply
              </button>
              {mine && (
                <>
                  <button onClick={() => startEdit(c)} className="text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`${testid}-edit-${c.id}`}>
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(c)} className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid={`${testid}-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </>
              )}
            </div>
          </>
        )}
        {node.children.map((ch) => renderNode(ch, depth + 1))}
      </div>
    );
  };

  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="label-eyebrow text-[10px] inline-flex items-center gap-2">
        <MessageSquare className="w-3 h-3" /> {title} · {rows.length}
      </div>

      {tree.length === 0 ? (
        <div className="text-xs text-muted-foreground border border-dashed border-border p-3" data-testid={`${testid}-empty`}>
          {emptyHint}
        </div>
      ) : (
        <div data-testid={`${testid}-list`}>
          {tree.map((n) => renderNode(n, 0))}
        </div>
      )}

      <form onSubmit={submit} className="space-y-2 pt-1">
        {replyTo && (
          <div className="text-[10px] text-muted-foreground inline-flex items-center gap-2 bg-muted/40 px-2 py-1" data-testid={`${testid}-reply-target`}>
            Replying to <span className="font-medium text-foreground">{replyTo.author_name}</span>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply"><X className="w-3 h-3" /></button>
          </div>
        )}
        <SmartRichText value={draft} onChange={setDraft} minRows={2} maxHeight={280} placeholder="Add a comment…" mediaMode="url-only" workspaceId={activeWorkspaceId} testid={`${testid}-composer`} />
        {extraToolbar && (
          <div className="flex items-center gap-2" data-testid={`${testid}-extra-toolbar`}>
            {typeof extraToolbar === "function" ? extraToolbar({ setDraft, draft }) : extraToolbar}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <Button type="submit" disabled={busy || !draft.trim()} className="rounded-none h-8 text-xs" data-testid={`${testid}-submit`}>
            <Send className="w-3 h-3 mr-1" /> Post
          </Button>
          {(entityType === "ticket" || entityType === "studio_booking") && (
            <label className="text-[11px] inline-flex items-center gap-1.5 cursor-pointer"
                   title={entityType === "studio_booking"
                     ? "Emails the customer with your comment"
                     : "Emails the requester and shows on the client portal"}>
              <input type="checkbox" checked={isExternal} onChange={(e) => setIsExternal(e.target.checked)}
                     data-testid={`${testid}-external-toggle`} />
              {entityType === "studio_booking" ? "Visible to customer (emails them)" : "External (visible to requester)"}
            </label>
          )}
          {draft && (
            <Button type="button" variant="ghost" className="rounded-none h-8 text-xs" onClick={() => { setDraft(""); setReplyTo(null); }} data-testid={`${testid}-clear`}>
              Clear
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

function buildTree(rows) {
  const byId = {};
  rows.forEach((r) => { byId[r.id] = { comment: r, children: [] }; });
  const roots = [];
  rows.forEach((r) => {
    if (r.parent_id && byId[r.parent_id]) byId[r.parent_id].children.push(byId[r.id]);
    else roots.push(byId[r.id]);
  });
  return roots;
}
